/**
 * The Support ticket's lifecycle controls, and the status badge they sit beside.
 *
 * Both live here because the queue and the ticket detail page now need the SAME two things, and
 * the badge in particular was previously redeclared per page — which is how two views of one
 * fact start disagreeing about what colour `in_progress` is.
 *
 * The buttons are driven entirely by `lib/supportLifecycle.ts`; this file decides only how an
 * action looks, never whether it exists.
 */

import {
  supportLifecycleActions,
  type SupportLifecycleAction,
  type SupportLifecycleActionId,
  type SupportStatus,
} from '@/lib/supportLifecycle';

/**
 * Status badge — moved here verbatim from SupportPage so the queue and the detail page render
 * the identical element. Colours and classes are unchanged.
 */
export function SupportStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    open: 'bg-plat-info-soft text-plat-info-text',
    in_progress: 'bg-plat-warning-soft text-plat-warning-text',
    resolved: 'bg-plat-success-soft text-plat-success-text',
    closed: 'bg-plat-neutral-soft text-plat-muted',
  };
  const label = status.replace(/_/g, ' ');
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${
        map[status] ?? 'bg-plat-neutral-soft text-plat-secondary'
      }`}
    >
      {label}
    </span>
  );
}

/**
 * The colour each action already had in the queue, kept so nothing shifts visually.
 * Keyed by action id rather than by target status: "Close" reads the same whether it follows a
 * resolution or discards a duplicate.
 */
export const SUPPORT_ACTION_TONE: Record<SupportLifecycleActionId, string> = {
  start: 'text-plat-info-text',
  resolve: 'text-plat-success-text',
  close: 'text-plat-secondary',
  reopen: 'text-plat-warning-text',
};

/**
 * The detail page's control group.
 *
 * Sized to match the priority `<select>` it sits next to (h-8, text-xs), so replacing the old
 * status dropdown changes the header's meaning without changing its shape. The primary action is
 * a filled button and the secondary is outlined: on a `resolved` ticket that is the difference
 * between "Close — the expected next step" and "Return to handling — available if the member
 * says it is still broken", which the two identical links it replaces could not express.
 */
export function SupportLifecycleActions({
  status,
  onAction,
  disabled = false,
}: {
  status: string;
  /** Receives the TARGET status; the caller issues the PATCH. */
  onAction: (to: SupportStatus, action: SupportLifecycleAction) => void;
  disabled?: boolean;
}) {
  const actions = supportLifecycleActions(status);
  if (actions.length === 0) return null;

  return (
    <div className="flex shrink-0 items-center gap-2">
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          disabled={disabled}
          onClick={() => onAction(action.to, action)}
          title={action.label}
          className={
            action.kind === 'primary'
              ? 'h-8 rounded-lg bg-plat-accent-solid px-3 text-xs font-medium text-plat-on-accent transition hover:bg-plat-accent-solid-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled'
              : `h-8 rounded-lg border border-plat-border bg-plat-bg px-3 text-xs font-medium transition hover:bg-plat-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus disabled:cursor-not-allowed disabled:text-plat-disabled ${SUPPORT_ACTION_TONE[action.id]}`
          }
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}
